import { useState, useEffect, useCallback } from "react";
import { signIn, signOut, getSession, onAuthStateChange, callPlatformAdmin } from "./supabaseApi";
import IdleTimeoutGuard from "./IdleTimeout";

// Entirely separate app tree from AKSApp (see src/index.js) — the operator
// isn't a member of any brokerage, so this deliberately doesn't touch
// App.jsx's tenant auth state machine or the brokerage-scoped users table.
// Authorization is enforced server-side in supabase/functions/platform-admin
// (an email allowlist); every call here can fail with {error:"Not authorized"}
// even after Supabase Auth itself accepts the sign-in.

const B = {
  red: "#A51C30", black: "#141414", charcoal: "#222222",
  gray: "#4A4A4A", silver: "#8A8A8A", light: "#E8E8E8",
  offWhite: "#F5F4F2", white: "#FFFFFF", green: "#16A34A", amber: "#B45309",
};

const inp = {
  padding: "9px 12px", borderRadius: 8, border: "1px solid " + B.light,
  fontSize: 13, fontFamily: "'DM Sans',sans-serif", background: B.white,
  color: B.black, outline: "none", boxSizing: "border-box", width: "100%",
};
const lbl = {
  fontSize: 11, fontWeight: 700, color: B.silver, letterSpacing: "0.06em",
  textTransform: "uppercase", display: "block", marginBottom: 5, fontFamily: "'DM Sans',sans-serif",
};
const btnPrimary = (disabled = false) => ({
  padding: "10px 20px", borderRadius: 8, border: "none",
  background: disabled ? B.light : B.red, color: disabled ? B.silver : B.white,
  cursor: disabled ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700,
  fontFamily: "'DM Sans',sans-serif",
});
const btnGhost = {
  padding: "9px 16px", borderRadius: 8, border: "1px solid " + B.light,
  background: B.white, color: B.black, cursor: "pointer", fontSize: 13,
  fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
};

function fmtMoney(cents) {
  return "$" + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ─── Sign-in ────────────────────────────────────────────────────────────────
function SignInScreen({ onError, error }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); onError("");
    try {
      await signIn(email, password);
      // What happens next (loading brokerages, or "not authorized") is the
      // parent's concern via its own onAuthStateChange listener — this
      // button's spinner should only reflect the sign-in call itself, so it
      // can't get stuck if that downstream load fails for an unrelated reason.
    } catch (err) {
      onError(err.message || "Sign in failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: B.black, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'DM Sans',sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <form onSubmit={handleSubmit} style={{ background: B.charcoal, borderRadius: 14, padding: 32, width: "100%", maxWidth: 380, border: "1px solid #2a2a2a" }}>
        <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800, color: B.white, fontFamily: "'Playfair Display',serif" }}>TxCoordinatorPro</h1>
        <p style={{ margin: "0 0 24px", color: B.silver, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>Platform Admin</p>
        <div style={{ marginBottom: 14 }}>
          <label style={{ ...lbl, color: B.silver }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
            style={{ ...inp, background: "#1E1E1E", border: "1px solid #333", color: B.white }} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={{ ...lbl, color: B.silver }}>Password</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
            style={{ ...inp, background: "#1E1E1E", border: "1px solid #333", color: B.white }} />
        </div>
        {error && <div style={{ color: "#F87171", fontSize: 12.5, marginBottom: 14 }}>{error}</div>}
        <button type="submit" disabled={loading} style={{ ...btnPrimary(loading), width: "100%" }}>{loading ? "Signing in…" : "Sign In"}</button>
      </form>
    </div>
  );
}

// ─── New brokerage form ─────────────────────────────────────────────────────
function NewBrokerageForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    name: "", slug: "", principalName: "", principalEmail: "",
    principalLicense: "", principalPhone: "", maxUsers: "", maxTransactions: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const result = await callPlatformAdmin("create_brokerage", {
        name: form.name, slug: form.slug,
        principalName: form.principalName, principalEmail: form.principalEmail,
        principalLicense: form.principalLicense || undefined, principalPhone: form.principalPhone || undefined,
        maxUsers: form.maxUsers ? Number(form.maxUsers) : undefined,
        maxTransactions: form.maxTransactions ? Number(form.maxTransactions) : undefined,
      });
      onCreated(result.brokerage);
    } catch (err) {
      setError(err.message || "Failed to create brokerage.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <form onSubmit={submit} style={{ background: B.white, borderRadius: 14, padding: 28, width: "100%", maxWidth: 480, maxHeight: "90vh", overflow: "auto", fontFamily: "'DM Sans',sans-serif" }}>
        <h2 style={{ margin: "0 0 18px", fontSize: 18, fontFamily: "'Playfair Display',serif", color: B.black }}>New Brokerage</h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={lbl}>Brokerage Name</label><input style={inp} value={form.name} onChange={e => set("name", e.target.value)} required /></div>
          <div><label style={lbl}>Slug</label><input style={inp} placeholder="acme-realty" value={form.slug} onChange={e => set("slug", e.target.value.toLowerCase())} required /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={lbl}>Principal Broker Name</label><input style={inp} value={form.principalName} onChange={e => set("principalName", e.target.value)} required /></div>
          <div><label style={lbl}>Principal Broker Email</label><input type="email" style={inp} value={form.principalEmail} onChange={e => set("principalEmail", e.target.value)} required /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={lbl}>License # (optional)</label><input style={inp} value={form.principalLicense} onChange={e => set("principalLicense", e.target.value)} /></div>
          <div><label style={lbl}>Phone (optional)</label><input style={inp} value={form.principalPhone} onChange={e => set("principalPhone", e.target.value)} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
          <div><label style={lbl}>Max Users (blank = unlimited)</label><input type="number" min="1" style={inp} value={form.maxUsers} onChange={e => set("maxUsers", e.target.value)} /></div>
          <div><label style={lbl}>Max Transactions (blank = unlimited)</label><input type="number" min="1" style={inp} value={form.maxTransactions} onChange={e => set("maxTransactions", e.target.value)} /></div>
        </div>

        {error && <div style={{ color: B.red, fontSize: 12.5, marginBottom: 14 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={onCancel} style={btnGhost} disabled={saving}>Cancel</button>
          <button type="submit" style={btnPrimary(saving)} disabled={saving}>{saving ? "Creating…" : "Create Brokerage"}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Brokerage detail (limits + payments) ──────────────────────────────────
function BrokerageDetail({ brokerage, onBack, onUpdated }) {
  const [limits, setLimits] = useState({
    maxUsers: brokerage.max_users ?? "", maxTransactions: brokerage.max_transactions ?? "",
    documentImportQuota: brokerage.document_import_quota ?? 0,
  });
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsError, setLimitsError] = useState("");

  const [payments, setPayments] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  async function toggleActive() {
    const nextActive = !brokerage.active;
    if (!window.confirm(
      nextActive
        ? `Resume ${brokerage.name}? Their users will be able to sign in again immediately.`
        : `Pause ${brokerage.name}? Every user at this brokerage will be signed out and unable to log in until resumed.`
    )) return;
    setTogglingActive(true);
    try {
      const { brokerage: updated } = await callPlatformAdmin("set_active", { brokerageId: brokerage.id, active: nextActive });
      onUpdated(updated);
    } catch (err) {
      alert(err.message || "Failed to update status.");
    } finally {
      setTogglingActive(false);
    }
  }

  const loadPayments = useCallback(async () => {
    try {
      const { payments } = await callPlatformAdmin("list_payments", { brokerageId: brokerage.id });
      setPayments(payments);
    } catch (err) {
      console.error(err);
      setPayments([]);
    }
  }, [brokerage.id]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  async function saveLimits() {
    setSavingLimits(true); setLimitsError("");
    try {
      const { brokerage: updated } = await callPlatformAdmin("update_limits", {
        brokerageId: brokerage.id,
        maxUsers: limits.maxUsers === "" ? null : Number(limits.maxUsers),
        maxTransactions: limits.maxTransactions === "" ? null : Number(limits.maxTransactions),
        documentImportQuota: Number(limits.documentImportQuota) || 0,
      });
      onUpdated(updated);
    } catch (err) {
      setLimitsError(err.message || "Failed to save limits.");
    } finally {
      setSavingLimits(false);
    }
  }

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 60px" }}>
      <button onClick={onBack} style={{ ...btnGhost, marginBottom: 20 }}>← All Brokerages</button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
            <h1 style={{ margin: 0, fontSize: 24, fontFamily: "'Playfair Display',serif", color: B.black }}>{brokerage.name}</h1>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
              background: brokerage.active ? "#DCFCE7" : "#FEE2E2",
              color: brokerage.active ? B.green : B.red,
            }}>{brokerage.active ? "Active" : "Paused"}</span>
          </div>
          <p style={{ margin: 0, color: B.silver, fontSize: 13 }}>{brokerage.slug}.txcoordinatorpro.com</p>
        </div>
        <button onClick={toggleActive} disabled={togglingActive} style={{
          ...btnGhost,
          color: brokerage.active ? B.red : B.green,
          borderColor: brokerage.active ? "#F3C2C9" : "#B9E4C9",
          opacity: togglingActive ? 0.6 : 1,
        }}>
          {togglingActive ? "Working…" : brokerage.active ? "Pause Brokerage" : "Resume Brokerage"}
        </button>
      </div>

      <div style={{ background: B.white, border: "1px solid " + B.light, borderRadius: 12, padding: 22, marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 16px", fontSize: 15, fontFamily: "'Playfair Display',serif", color: B.black }}>Plan Limits</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
          <div>
            <label style={lbl}>Max Users</label>
            <input type="number" min="1" style={inp} placeholder="Unlimited" value={limits.maxUsers} onChange={e => setLimits(l => ({ ...l, maxUsers: e.target.value }))} />
          </div>
          <div>
            <label style={lbl}>Max Transactions</label>
            <input type="number" min="1" style={inp} placeholder="Unlimited" value={limits.maxTransactions} onChange={e => setLimits(l => ({ ...l, maxTransactions: e.target.value }))} />
            <span style={{ fontSize: 11, color: B.silver }}>Currently {brokerage.transaction_count} used</span>
          </div>
          <div>
            <label style={lbl}>Transaction Genie Quota</label>
            <input type="number" min="0" style={inp} value={limits.documentImportQuota} onChange={e => setLimits(l => ({ ...l, documentImportQuota: e.target.value }))} />
            <span style={{ fontSize: 11, color: B.silver }}>{brokerage.document_import_used} used (permanent)</span>
          </div>
        </div>
        {limitsError && <div style={{ color: B.red, fontSize: 12.5, marginBottom: 12 }}>{limitsError}</div>}
        <button onClick={saveLimits} disabled={savingLimits} style={btnPrimary(savingLimits)}>{savingLimits ? "Saving…" : "Save Limits"}</button>
      </div>

      <div style={{ background: B.white, border: "1px solid " + B.light, borderRadius: 12, padding: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontFamily: "'Playfair Display',serif", color: B.black }}>Payments</h2>
          <button onClick={() => setShowPaymentForm(true)} style={btnPrimary(false)}>+ Log Payment</button>
        </div>

        {payments === null && <div style={{ color: B.silver, fontSize: 13 }}>Loading…</div>}
        {payments?.length === 0 && <div style={{ color: B.silver, fontSize: 13 }}>No payments logged yet.</div>}
        {payments?.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: B.silver, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                <th style={{ padding: "6px 8px 10px 0", fontWeight: 700 }}>Date</th>
                <th style={{ padding: "6px 8px 10px 0", fontWeight: 700 }}>Amount</th>
                <th style={{ padding: "6px 8px 10px 0", fontWeight: 700 }}>Description</th>
                <th style={{ padding: "6px 8px 10px 0", fontWeight: 700 }}>Note</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id} style={{ borderTop: "1px solid " + B.light }}>
                  <td style={{ padding: "10px 8px 10px 0", color: B.black }}>{fmtDate(p.paid_at)}</td>
                  <td style={{ padding: "10px 8px 10px 0", color: B.green, fontWeight: 700 }}>{fmtMoney(p.amount_cents)}</td>
                  <td style={{ padding: "10px 8px 10px 0", color: B.black }}>{p.description}</td>
                  <td style={{ padding: "10px 8px 10px 0", color: B.silver }}>{p.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showPaymentForm && (
        <LogPaymentForm
          brokerageId={brokerage.id}
          onLogged={() => { setShowPaymentForm(false); loadPayments(); }}
          onCancel={() => setShowPaymentForm(false)}
        />
      )}
    </div>
  );
}

function LogPaymentForm({ brokerageId, onLogged, onCancel }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await callPlatformAdmin("log_payment", {
        brokerageId, amountCents: Math.round(Number(amount) * 100), description, paidAt, note: note || undefined,
      });
      onLogged();
    } catch (err) {
      setError(err.message || "Failed to log payment.");
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <form onSubmit={submit} style={{ background: B.white, borderRadius: 14, padding: 28, width: "100%", maxWidth: 420, fontFamily: "'DM Sans',sans-serif" }}>
        <h2 style={{ margin: "0 0 18px", fontSize: 18, fontFamily: "'Playfair Display',serif", color: B.black }}>Log Payment</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><label style={lbl}>Amount (USD)</label><input type="number" step="0.01" min="0" style={inp} value={amount} onChange={e => setAmount(e.target.value)} required /></div>
          <div><label style={lbl}>Date</label><input type="date" style={inp} value={paidAt} onChange={e => setPaidAt(e.target.value)} required /></div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Description</label>
          <input style={inp} placeholder="Starter plan — Oct 2026" value={description} onChange={e => setDescription(e.target.value)} required />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={lbl}>Note (optional)</label>
          <input style={inp} value={note} onChange={e => setNote(e.target.value)} />
        </div>
        {error && <div style={{ color: B.red, fontSize: 12.5, marginBottom: 14 }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={onCancel} style={btnGhost} disabled={saving}>Cancel</button>
          <button type="submit" style={btnPrimary(saving)} disabled={saving}>{saving ? "Saving…" : "Log Payment"}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Brokerage list ─────────────────────────────────────────────────────────
function BrokerageList({ brokerages, onSelect, onShowNewForm }) {
  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 24px 60px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontFamily: "'Playfair Display',serif", color: B.black }}>Brokerages</h1>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: B.silver }}>{brokerages.length} brokerage{brokerages.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={onShowNewForm} style={btnPrimary(false)}>+ New Brokerage</button>
      </div>

      <div style={{ background: B.white, border: "1px solid " + B.light, borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", background: B.offWhite, color: B.silver, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <th style={{ padding: "10px 14px" }}>Name</th>
              <th style={{ padding: "10px 14px" }}>Status</th>
              <th style={{ padding: "10px 14px" }}>Transactions</th>
              <th style={{ padding: "10px 14px" }}>Users</th>
              <th style={{ padding: "10px 14px" }}>Genie Imports</th>
              <th style={{ padding: "10px 14px" }}>Last Payment</th>
            </tr>
          </thead>
          <tbody>
            {brokerages.map(b => (
              <tr key={b.id} onClick={() => onSelect(b)} style={{ borderTop: "1px solid " + B.light, cursor: "pointer" }}>
                <td style={{ padding: "12px 14px" }}>
                  <div style={{ fontWeight: 700, color: B.black }}>{b.name}</div>
                  <div style={{ fontSize: 11.5, color: B.silver }}>{b.slug}.txcoordinatorpro.com</div>
                </td>
                <td style={{ padding: "12px 14px" }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                    background: b.active ? "#DCFCE7" : "#FEE2E2", color: b.active ? B.green : B.red,
                  }}>{b.active ? "Active" : "Paused"}</span>
                </td>
                <td style={{ padding: "12px 14px", color: B.black }}>{b.transaction_count}{b.max_transactions != null ? ` / ${b.max_transactions}` : ""}</td>
                <td style={{ padding: "12px 14px", color: B.black }}>{b.max_users != null ? `up to ${b.max_users}` : "Unlimited"}</td>
                <td style={{ padding: "12px 14px", color: B.black }}>{b.document_import_used} / {b.document_import_quota}</td>
                <td style={{ padding: "12px 14px", color: b.last_payment_at ? B.black : B.silver }}>{fmtDate(b.last_payment_at)}</td>
              </tr>
            ))}
            {brokerages.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: B.silver }}>No brokerages yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Root ───────────────────────────────────────────────────────────────────
export default function PlatformAdmin() {
  const [authState, setAuthState] = useState("loading"); // loading | signed_out | not_authorized | ready
  const [authError, setAuthError] = useState("");
  const [brokerages, setBrokerages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);

  const loadBrokerages = useCallback(async () => {
    try {
      const { brokerages } = await callPlatformAdmin("list_brokerages");
      setBrokerages(brokerages);
      setAuthState("ready");
    } catch (err) {
      if (err.message === "Not authorized") setAuthState("not_authorized");
      else setAuthError(err.message || "Failed to load brokerages.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSession().then((session) => {
      if (cancelled) return;
      if (session) loadBrokerages(); else setAuthState("signed_out");
    });
    const { data: { subscription } } = onAuthStateChange((session) => {
      if (session) loadBrokerages(); else setAuthState("signed_out");
    });
    return () => { cancelled = true; subscription.unsubscribe(); };
  }, [loadBrokerages]);

  if (authState === "loading") {
    return <div style={{ minHeight: "100vh", background: B.black }} />;
  }
  if (authState === "signed_out") {
    return <SignInScreen error={authError} onError={setAuthError} />;
  }
  if (authState === "not_authorized") {
    return (
      <div style={{ minHeight: "100vh", background: B.black, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif", padding: 20 }}>
        <p style={{ color: B.white, fontSize: 15, marginBottom: 16 }}>This account isn't authorized for platform admin.</p>
        <button onClick={() => signOut()} style={{ ...btnGhost, background: "transparent", color: B.white, borderColor: "#333" }}>Sign Out</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: B.offWhite, fontFamily: "'DM Sans',sans-serif" }}>
      <IdleTimeoutGuard onTimeout={() => {
        setAuthError("You were signed out due to inactivity.");
        signOut();
      }}/>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ background: B.black, padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <span style={{ color: B.white, fontFamily: "'Playfair Display',serif", fontWeight: 800, fontSize: 16 }}>TxCoordinatorPro <span style={{ color: B.silver, fontWeight: 500, fontSize: 12 }}>· Platform Admin</span></span>
        <button onClick={() => signOut()} style={{ ...btnGhost, background: "transparent", color: B.silver, borderColor: "#333" }}>Sign Out</button>
      </div>

      {selected ? (
        <BrokerageDetail
          brokerage={selected}
          onBack={() => { setSelected(null); loadBrokerages(); }}
          onUpdated={(updated) => setSelected(s => ({ ...s, ...updated }))}
        />
      ) : (
        <BrokerageList brokerages={brokerages} onSelect={setSelected} onShowNewForm={() => setShowNewForm(true)} />
      )}

      {showNewForm && (
        <NewBrokerageForm
          onCreated={() => { setShowNewForm(false); loadBrokerages(); }}
          onCancel={() => setShowNewForm(false)}
        />
      )}
    </div>
  );
}
